import { advanceBillingDate } from "./nextPaymentDate.js";

export { getNextPaymentDate, advanceBillingDate } from "./nextPaymentDate.js";

// Plan interest / plan-selection labels — aligned with client decision table
export const PLAN_INTEREST = {
  CDL: "CDL",
  IDENTITY: "Identity Defender",
  BOTH: "Both",
  STUDENT: "Student Defender",
  STUDENT_ID_GOLD_IND:
    "Student Defender & Identity Defender Gold – Individual",
  STUDENT_ID_PLAT_IND:
    "Student Defender & Identity Defender Platinum – Individual",
};

export const WHO_FOR = {
  STUDENT: "Your student",
  YOURSELF: "Yourself",
  FAMILY: "You and your family",
};

const norm = (g) => (g?.description || "").toLowerCase();
const isBundle = (g) => String(g?.plan_id || "").includes(",");
const isCDLPlan = (g) =>
  g?.planType === "CDL" ||
  (g?.plan_code || "").toUpperCase().includes("CDL") ||
  norm(g).includes("cdl defender");
const isStudentPlan = (g) => {
  if (g?.planType === "STUDENT") return true;
  const d = norm(g);
  if (
    d.includes("student defender") ||
    d.includes("student plan") ||
    d.includes("college defender")
  ) {
    return true;
  }
  const code = (g?.plan_code || "").toUpperCase();
  return code.includes("STUDENT") || code.includes("COLLEGE");
};
const isIdentityPlan = (g) =>
  g?.planType === "IDD" || norm(g).includes("identity defender");
const isIndividualPlan = (g) => norm(g).includes("individual");
const isGoldPlan = (g) => norm(g).includes("gold");
const isPlatinumPlan = (g) => norm(g).includes("platinum");

const isStandaloneCDL = (g) =>
  isCDLPlan(g) && !isBundle(g) && !isStudentPlan(g) && !isIdentityPlan(g);

const isIdGoldIndividual = (g) =>
  isIdentityPlan(g) && !isBundle(g) && isGoldPlan(g) && isIndividualPlan(g);
const isIdPlatinumIndividual = (g) =>
  isIdentityPlan(g) && !isBundle(g) && isPlatinumPlan(g) && isIndividualPlan(g);
const isIdGoldFamily = (g) =>
  isIdentityPlan(g) &&
  !isBundle(g) &&
  isGoldPlan(g) &&
  !isIndividualPlan(g) &&
  !norm(g).includes("student");
const isIdPlatinumFamily = (g) =>
  isIdentityPlan(g) &&
  !isBundle(g) &&
  isPlatinumPlan(g) &&
  !isIndividualPlan(g) &&
  !norm(g).includes("student");

const isCdlIdBundle = (g, individual) =>
  isBundle(g) &&
  isCDLPlan(g) &&
  isIdentityPlan(g) &&
  (individual ? isIndividualPlan(g) : !isIndividualPlan(g));

const normalizeInterest = (interest) => {
  const value = (interest || "").trim();
  if (value === "CDL Defender") return PLAN_INTEREST.CDL;
  if (value === "All") return PLAN_INTEREST.BOTH;
  return value;
};

/**
 * Decision table (060826 - Updates to General Enrollment Form Question Logic):
 *
 * | Who for      | CDL | Plan interest      | Parameter | Plans shown                                      |
 * |--------------|-----|--------------------|-----------|--------------------------------------------------|
 * | Your student | —   | —                  | student   | Student Defender; Student+ID Gold/Plat Ind       |
 * | Yourself     | Yes | CDL                | general   | CDL Defender                                     |
 * | Yourself     | Yes | Identity Defender  | general   | ID Gold/Platinum Individual                      |
 * | Yourself     | Yes | Both               | general   | CDL+ID Gold/Platinum Individual                  |
 * | Yourself     | No  | Student Defender   | student   | Student Defender                                 |
 * | Yourself     | No  | Identity Defender  | general   | ID Gold/Platinum Individual                      |
 * | Yourself     | No  | Both               | student   | Student+ID Gold/Platinum Individual              |
 * | Family       | Yes | CDL                | general   | CDL Defender                                     |
 * | Family       | Yes | Identity Defender  | general   | ID Gold/Platinum (family)                        |
 * | Family       | Yes | Both               | general   | CDL+ID Gold/Platinum (family)                    |
 * | Family       | No  | Student Defender   | student   | Student Defender                                 |
 * | Family       | No  | Identity Defender  | general   | ID Gold/Platinum (family)                        |
 * | Family       | No  | Both               | —         | (not offered)                                    |
 *
 * Returns `student` | `general` | null (invalid per spec).
 */
export const getEnrollmentParameter = (state) => {
  if (state?.opt_type === "student") return "student";
  if (state?.opt_type !== "general") return state?.opt_type || "general";

  const whoFor = state?.whoFor;
  const hasCDL = state?.hasCDL;
  const interest = normalizeInterest(state?.planInterest);

  // Branch A — Your student: parameter always student (any plan selection)
  if (whoFor === WHO_FOR.STUDENT) return "student";

  if (whoFor === WHO_FOR.YOURSELF || whoFor === WHO_FOR.FAMILY) {
    if (hasCDL === "yes") return "general";

    if (hasCDL === "no") {
      // Family + No + Both is undefined in spec — not offered in UI
      if (whoFor === WHO_FOR.FAMILY && interest === PLAN_INTEREST.BOTH) {
        return null;
      }
      if (interest === PLAN_INTEREST.STUDENT || interest === PLAN_INTEREST.BOTH) {
        return "student";
      }
      if (
        interest === PLAN_INTEREST.IDENTITY ||
        interest === PLAN_INTEREST.STUDENT_ID_GOLD_IND ||
        interest === PLAN_INTEREST.STUDENT_ID_PLAT_IND
      ) {
        return "general";
      }
    }
  }

  return "general";
};

/** Full student enrollment form — same paths as student parameter (school name, student address, billing). */
export const isStudentFlow = (state) => getEnrollmentParameter(state) === "student";

/** API/billing parameter from questionnaire — `student` or `general`. */
export const usesStudentParameter = (state) =>
  getEnrollmentParameter(state) === "student";

export const shouldSkipProrated = (state) => usesStudentParameter(state);

export const effectiveOptType = (state) => {
  if (state?.opt_type === "student") return "student";
  if (state?.opt_type === "general") {
    const param = getEnrollmentParameter(state);
    return param === "student" ? "student" : "general";
  }
  return state?.opt_type || "";
};

export const shouldShowPlanInterest = (state) => {
  if (!state?.whoFor) return false;
  if (state.whoFor === WHO_FOR.STUDENT) return false;
  return state.hasCDL === "yes" || state.hasCDL === "no";
};

export const getPlanInterestQuestionNumber = () => "3";

const normalizeCampaign = (str) => (str || "").toString().trim().toLowerCase();

/** Comma-separated opt types / plan campaign tags from admin settings. */
export const parseExcludeCampaigns = (raw) => {
  if (raw === undefined || raw === null || raw === "") return [];
  return String(raw)
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
};

const campaignTags = (raw) => {
  const normalized = normalizeCampaign(raw);
  if (!normalized) return [];
  return normalized.split(/[,|]/).map((t) => t.trim()).filter(Boolean);
};

/** True when a plan or bundle campaign tag matches an exclude entry. */
export const planMatchesExcludeCampaign = (group, excludeCampaigns) => {
  if (!group || !excludeCampaigns?.length) return false;
  const tags = [
    ...campaignTags(group.campaign),
    ...campaignTags(group.bundle_campaign),
  ];
  return excludeCampaigns.some((ex) => tags.includes(ex));
};

/** Waive CC service fee when opt_type or selected/plan campaign matches admin exclude list. */
export const isServiceFeeWaived = (state, excludeRaw, planGroup = null) => {
  const excludes = parseExcludeCampaigns(excludeRaw ?? state?.processing_fee_exclude_campaign);
  if (!excludes.length) return false;

  const optType = normalizeCampaign(effectiveOptType(state) || state?.opt_type);
  if (excludes.includes(optType)) return true;

  if (planGroup && planMatchesExcludeCampaign(planGroup, excludes)) return true;

  const selected = (state?.bundled_group || []).filter((g) => g.select_plan);
  return selected.some((g) => planMatchesExcludeCampaign(g, excludes));
};

/** Effective CC service fee for a plan row (0 when waived). */
export const resolveServiceFeeAmount = (state, planGroup = null) => {
  if (state?.payment_method !== "CC") return 0;
  const base =
    parseFloat(state.processing_fee_base ?? state.service_fee) || 0;
  if (isServiceFeeWaived(state, state?.processing_fee_exclude_campaign, planGroup)) {
    return 0;
  }
  return base;
};

/** Plan or bundle tagged with student campaign in admin. */
export const hasStudentCampaign = (g) => {
  const campaign = normalizeCampaign(g?.campaign);
  const bundleCampaign = normalizeCampaign(g?.bundle_campaign);
  return campaign.includes("student") || bundleCampaign.includes("student");
};

/** Student-branch plans — filtered by admin campaign tag only. */
export const filterYourStudentBranchPlans = (plans) =>
  plans.filter(hasStudentCampaign);

/**
 * Q3 options for Yourself / Family branches only.
 */
export const getPlanInterestOptions = (state) => {
  const { whoFor, hasCDL } = state || {};

  if (whoFor === WHO_FOR.YOURSELF) {
    if (hasCDL === "yes") {
      return [PLAN_INTEREST.CDL, PLAN_INTEREST.IDENTITY, PLAN_INTEREST.BOTH];
    }
    if (hasCDL === "no") {
      return [PLAN_INTEREST.STUDENT, PLAN_INTEREST.IDENTITY, PLAN_INTEREST.BOTH];
    }
  }

  if (whoFor === WHO_FOR.FAMILY) {
    if (hasCDL === "yes") {
      return [PLAN_INTEREST.CDL, PLAN_INTEREST.IDENTITY, PLAN_INTEREST.BOTH];
    }
    if (hasCDL === "no") {
      // Spec: no Both option for Family + No CDL
      return [PLAN_INTEREST.STUDENT, PLAN_INTEREST.IDENTITY];
    }
  }

  return [];
};

export const canProceedFromQuestions = (state) => {
  if (!state?.whoFor) return false;
  if (state.whoFor === WHO_FOR.STUDENT) return true;
  if (!shouldShowPlanInterest(state)) return false;
  if (!state?.planInterest) return false;
  if (getEnrollmentParameter(state) === null) return false;
  return true;
};

export const shouldApplyQuestionPlanFilter = (state) => {
  if (state?.opt_type !== "general") return false;
  if (state?.whoFor === WHO_FOR.STUDENT) return true;
  return !!state?.planInterest;
};

export const isStudentPlanGroup = (g) => isStudentPlan(g);

export const isStudentIdentityBundleGroup = (g) =>
  isBundle(g) && isStudentPlan(g) && isIdentityPlan(g);

export const shouldUseStudentPricing = (state) => {
  if (state?.opt_type === "student") return true;
  if (getEnrollmentParameter(state) !== "student") return false;

  const interest = normalizeInterest(state?.planInterest);
  if (state?.whoFor === WHO_FOR.STUDENT) return true;

  return (
    interest === PLAN_INTEREST.STUDENT ||
    interest === PLAN_INTEREST.BOTH ||
    interest === PLAN_INTEREST.STUDENT_ID_GOLD_IND ||
    interest === PLAN_INTEREST.STUDENT_ID_PLAT_IND
  );
};

/** Standalone student plan — full student annual discount on entire premium. */
export const usesStudentPlanPricing = (state, group) =>
  shouldUseStudentPricing(state) &&
  isStudentPlanGroup(group) &&
  !isStudentIdentityBundleGroup(group);

/** Student + Identity bundle — annual discount applies to both plan portions. */
export const usesMixedStudentBundlePricing = (state, group) =>
  shouldUseStudentPricing(state) && isStudentIdentityBundleGroup(group);

/** Format plan price for display — always two decimal places (e.g. 38.6 → 38.60). */
export const formatPlanPriceDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "0.00";
  const str = String(value).trim();
  if (str.includes("+")) {
    return str
      .split("+")
      .map((part) => {
        const num = parseFloat(String(part).replace(/[^0-9.-]/g, ""));
        return Number.isFinite(num) ? num.toFixed(2) : String(part).trim();
      })
      .join(" + ");
  }
  const num = parseFloat(str.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(num) ? num.toFixed(2) : str;
};

export const usesStudentBillingPricing = (state, group) =>
  usesStudentPlanPricing(state, group) ||
  usesMixedStudentBundlePricing(state, group);

export const getGeneralNextRecurringDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  date.setDate(20);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const getStudentEnrollmentDate = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

export const getStudentEffectiveDate = (enrollmentDate = getStudentEnrollmentDate()) => {
  const date = new Date(enrollmentDate);
  date.setDate(date.getDate() + 1);
  date.setHours(0, 0, 0, 0);
  return date;
};

/** First recurring charge — enrollment anniversary (+1 year or +1 month), not effective date. */
export const getStudentNextRecurringDate = (
  enrollmentDate = getStudentEnrollmentDate(),
  billingPeriod = "monthly"
) => {
  const unit = billingPeriod === "annual" ? "year" : "month";
  return advanceBillingDate(enrollmentDate, enrollmentDate, 1, unit).toDate();
};

export const isIdentityPlanGroup = (group) => isIdentityPlan(group);

export const applyStudentCampaignFilter = (plans, state) => {
  if (getEnrollmentParameter(state) !== "student") return plans;
  return plans.filter(hasStudentCampaign);
};

/**
 * Filter plans to match the "Plans shown" column in the decision table.
 */
export const filterPlansByQuestions = (plans, state) => {
  const hasCDL = state?.hasCDL;
  const planInterest = normalizeInterest(state?.planInterest);
  const whoFor = state?.whoFor;
  const isYourself = whoFor === WHO_FOR.YOURSELF;
  const isFamily = whoFor === WHO_FOR.FAMILY;

  // Branch A — Your student: no Q2; show all student-campaign plans
  if (whoFor === WHO_FOR.STUDENT) {
    return filterYourStudentBranchPlans(plans);
  }

  const isCdlInterest = planInterest === PLAN_INTEREST.CDL;
  const isBothInterest = planInterest === PLAN_INTEREST.BOTH;
  const isIdentityInterest = planInterest === PLAN_INTEREST.IDENTITY;
  const isStudentInterest = planInterest === PLAN_INTEREST.STUDENT;

  // Branch B/C — CDL Yes (parameter: general)
  if (hasCDL === "yes") {
    if (isCdlInterest) {
      return plans.filter(isStandaloneCDL);
    }
    if (isIdentityInterest) {
      if (isYourself) {
        return plans.filter((g) => isIdGoldIndividual(g) || isIdPlatinumIndividual(g));
      }
      if (isFamily) {
        return plans.filter((g) => isIdGoldFamily(g) || isIdPlatinumFamily(g));
      }
    }
    if (isBothInterest) {
      if (isYourself) {
        return plans.filter((g) => isCdlIdBundle(g, true));
      }
      if (isFamily) {
        return plans.filter((g) => isCdlIdBundle(g, false));
      }
    }
    return [];
  }

  // Branch B/C — CDL No (student parameter paths use campaign tag only)
  if (hasCDL === "no") {
    if (isBothInterest && isYourself) {
      return plans.filter((g) => isStudentIdentityBundleGroup(g));
    }
    if (isStudentInterest) {
      return plans.filter((g) => isStudentPlan(g) && !isBundle(g));
    }
    if (isIdentityInterest) {
      if (isYourself) {
        return plans.filter((g) => isIdGoldIndividual(g) || isIdPlatinumIndividual(g));
      }
      if (isFamily) {
        return plans.filter((g) => isIdGoldFamily(g) || isIdPlatinumFamily(g));
      }
    }
    return [];
  }

  return [];
};

/** Standalone Student Defender (not bundled with Identity). */
const isStandaloneStudentPlan = (g) => isStudentPlan(g) && !isBundle(g);

const getStudentPlanDisplayRank = (g) => {
  if (isStandaloneStudentPlan(g)) return 0;
  if (isStudentIdentityBundleGroup(g)) return 1;
  return 2;
};

/**
 * Select a Plan — student pricing: standalone Student Defender left, Student+Identity bundles right.
 * Other plans keep stable relative order after those groups.
 */
export const sortStudentPlanDisplayOrder = (plans, state) => {
  if (!shouldUseStudentPricing(state) || !Array.isArray(plans) || plans.length < 2) {
    return plans;
  }

  return [...plans].sort((a, b) => {
    const rankDiff = getStudentPlanDisplayRank(a) - getStudentPlanDisplayRank(b);
    if (rankDiff !== 0) return rankDiff;
    return norm(a).localeCompare(norm(b));
  });
};
